/*
 * ez-lens-popup — the read-only editorial lens popup (sections/ez-lens-tiles.liquid).
 *
 * Vanilla custom element, no library. Owns the FIVE overlay a11y non-negotiables
 * (design_system.md §6 3.1, inherited by every modal/drawer/sheet we ship):
 *   1. focus-trap while open      4. body scroll-lock while open
 *   2. Esc closes                 5. return-focus to the trigger on close
 *   3. backdrop-click closes
 *
 * The popup has ZERO input controls by construction — it renders no form elements, so there is
 * nothing here that could grow into a configurator. The only commit action is the SHOP link.
 *
 * Openers are DOCUMENT-DELEGATED so a Theme-Editor block re-render never orphans them, and each
 * opener resolves its panel by block key with a DOM-ordinal fallback (a key miss would otherwise
 * silently pin every tile to panel 1 — a failure that looks like nothing at all).
 */
(function () {
  if (customElements.get('ez-lens-popup')) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class EzLensPopup extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      // ADOPT the panels. Each panel is rendered by its own BLOCK file (bug-157: a section
      // cannot read block settings in a parallel loop when it renders children with
      // content_for), so it arrives nested inside its tile <li> — inside the row's
      // overflow-scroll box. Move each one up into this container, which sits outside every
      // clipping ancestor, so a position:fixed overlay is never clipped or scroll-trapped.
      this.scope = this.closest('.shopify-section') || this.parentElement || document;
      var found = Array.prototype.slice.call(this.scope.querySelectorAll('[data-ez-lens-panel]'));
      for (var i = 0; i < found.length; i++) {
        if (found[i].parentElement !== this) this.appendChild(found[i]);
      }

      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ez-lens-panel]'));
      this.activePanel = null;
      this.trigger = null;

      this._onKeydown = this._onKeydown.bind(this);

      // Close: the backdrop, the X button, anything marked data-ez-lens-close.
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-ez-lens-close]')) {
          e.preventDefault();
          this.close();
        }
      }.bind(this));
    }

    // Re-adopt any panel that appeared after boot (a Theme-Editor edit re-renders ONE block,
    // and the fresh panel lands back inside its tile) so the key lookup can never go stale.
    _resync() {
      var found = Array.prototype.slice.call(this.scope.querySelectorAll('[data-ez-lens-panel]'));
      for (var i = 0; i < found.length; i++) {
        if (found[i].parentElement !== this) this.appendChild(found[i]);
      }
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ez-lens-panel]'));
    }

    _find(key) {
      for (var i = 0; i < this.panels.length; i++) {
        if (this.panels[i].getAttribute('data-ez-lens-panel') === key) return this.panels[i];
      }
      return null;
    }

    open(key, trigger, fallbackIndex) {
      var panel = this._find(key);
      if (!panel) { this._resync(); panel = this._find(key); }
      if (!panel && typeof fallbackIndex === 'number') panel = this.panels[fallbackIndex];
      if (!panel) return;

      this.trigger = trigger || null;
      this.activePanel = panel;

      this.hidden = false;
      panel.hidden = false;

      // (4) body scroll-lock — hold the scroll position so closing does not jump the page.
      this._scrollY = window.scrollY;
      document.body.style.top = '-' + this._scrollY + 'px';
      document.body.classList.add('ez-lens-locked');

      document.addEventListener('keydown', this._onKeydown);

      // (1) focus INTO the panel; the trap keeps it there.
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
      panel.scrollTop = 0;
      var scroller = panel.querySelector('.ez-lens-pop__scroll');
      if (scroller) scroller.scrollTop = 0;
    }

    close() {
      if (this.hidden) return;

      if (this.activePanel) this.activePanel.hidden = true;
      this.hidden = true;
      this.activePanel = null;

      document.removeEventListener('keydown', this._onKeydown);

      document.body.classList.remove('ez-lens-locked');
      document.body.style.top = '';
      window.scrollTo(0, this._scrollY || 0);

      // (5) return focus to the trigger.
      if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus();
      this.trigger = null;
    }

    _onKeydown(e) {
      // (2) Esc closes.
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab' || !this.activePanel) return;

      // (1) focus-trap: cycle within the open panel.
      var nodes = Array.prototype.filter.call(
        this.activePanel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null || el === document.activeElement; }
      );
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  customElements.define('ez-lens-popup', EzLensPopup);

  // Document-delegated openers: survive a Theme-Editor block re-render + any list morph.
  if (!window.__ezLensOpenersBound) {
    window.__ezLensOpenersBound = true;
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-ez-lens-open]');
      if (!opener) return;
      var section = opener.closest('.shopify-section, section') || document;
      var popup = section.querySelector('ez-lens-popup') || document.querySelector('ez-lens-popup');
      if (!popup || typeof popup.open !== 'function') return;
      e.preventDefault();
      var all = Array.prototype.slice.call(section.querySelectorAll('[data-ez-lens-open]'));
      popup.open(opener.getAttribute('data-ez-lens-open'), opener, all.indexOf(opener));
    });
  }
})();
